"use client";
import { useState, useEffect, useRef } from 'react';
import { z } from 'zod';
import Link from 'next/link';
import { getSupabaseClient } from '@/lib/db/supabaseClient';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AnimatedBackground } from '@/components/AnimatedBackground';
import { useRouter } from 'next/navigation';
import { translateError } from '@/lib/utils/errorTranslator';

const Schema = z.object({ 
	email: z.string().email('Ogiltig e-postadress'), 
	password: z.string().min(6, 'Lösenordet måste vara minst 6 tecken') 
});

function LoginForm() {
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
		const [oauthLoading, setOauthLoading] = useState<null | 'google' | 'azure'>(null);
	const [visible, setVisible] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const formRef = useRef<HTMLDivElement>(null);
	const router = useRouter();
	const [redirectPath, setRedirectPath] = useState('/dashboard');
	const [loginType, setLoginType] = useState<string | null>(null);
	const [isLogout, setIsLogout] = useState(false);
	const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
	const [oauthErrorParam, setOauthErrorParam] = useState<string | null>(null);

	useEffect(() => {
		setVisible(true);

		// Read URL params client-side to avoid production hook edge-cases around useSearchParams
		try {
			const params = new URLSearchParams(window.location.search);
			setRedirectPath(params.get('redirect') || '/dashboard');
			setLoginType(params.get('type'));
			setIsLogout(params.get('logout') === 'true');
			setPasswordResetSuccess(params.get('password_reset') === 'success');
			setOauthErrorParam(params.get('error'));
		} catch {
			// ignore
		}
		
		// If coming from logout, skip auto-login check
		if (isLogout) {
			// Clear any remaining session data
			const supabase = getSupabaseClient();
			supabase.auth.signOut().catch(() => {
				// Ignore errors - we're already logging out
			});
			return;
		}
		
		// Check if user is already logged in (only if not coming from logout)
		async function checkSession() {
			const supabase = getSupabaseClient();
			const { data } = await supabase.auth.getSession();
			
			// If already logged in, check if company user and redirect accordingly
			if (data.session?.access_token) {
				try {
					const companyResponse = await fetch('/api/user/company', {
						headers: {
							'Authorization': `Bearer ${data.session.access_token}`
						}
					});
					
					if (companyResponse.ok) {
						const companyData = await companyResponse.json();
						if (companyData.isCompanyUser && companyData.company?.id) {
							window.location.href = `/company/${companyData.company.id}`;
							return;
						}
					}
				} catch (error) {
					console.error('Error checking company user:', error);
				}
				
				// Regular user, redirect to dashboard
				window.location.href = redirectPath;
			}
		}
		
		checkSession();
	}, [redirectPath, isLogout]);

	useEffect(() => {
		if (!oauthErrorParam) return;
		if (oauthErrorParam === 'oauth_email_exists') {
			setError('Det finns redan ett konto med den här e-postadressen. Logga in med ditt lösenord.');
		} else {
			setError('Inloggningen misslyckades. Försök igen.');
		}
	}, [oauthErrorParam]);

	async function handleOAuthSignIn(provider: 'google' | 'azure') {
		setError(null);
		setOauthLoading(provider);
		try {
			const supabase = getSupabaseClient();
			const redirectTo = `${window.location.origin}/auth/callback`;
			const { error } = await supabase.auth.signInWithOAuth({
				provider,
				options: { redirectTo },
			});
			if (error) throw error;
		} catch (e: any) {
			setError(translateError(e, 'sv'));
			setOauthLoading(null);
		}
	}

	async function onSubmit(formData: FormData) {
		setError(null);
		setLoading(true);
		try {
			const parsed = Schema.parse({ email: formData.get('email'), password: formData.get('password') });
			const supabase = getSupabaseClient();
			const { data, error } = await supabase.auth.signInWithPassword({ 
				email: parsed.email, 
				password: parsed.password 
			});
			
			if (error) throw error;
			
			// Check if user is a company user
			const { data: { session } } = await supabase.auth.getSession();
			if (session?.access_token) {
				const companyResponse = await fetch('/api/user/company', {
					headers: {
						'Authorization': `Bearer ${session.access_token}`
					}
				});
				
				if (companyResponse.ok) {
					const companyData = await companyResponse.json();
					if (companyData.isCompanyUser && companyData.company?.id) {
						// Redirect to company dashboard
						window.location.href = `/company/${companyData.company.id}`;
						return;
					}
				}
			}
			
			// Redirect to regular dashboard or specified redirect path
			window.location.href = redirectPath;
		} catch (e: any) {
			console.error("Login error:", e);
			setError(e.message ?? 'Något blev fel');
		} finally {
			setLoading(false);
		}
	}

	return (
		<main className="min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-12 sm:py-20 relative overflow-x-hidden">
			<AnimatedBackground />
			
			<header className="relative z-10 w-full flex justify-between items-center p-4 sm:p-6 absolute top-0">
				<Link 
					href="/" 
					className={`transition-all duration-700 flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}
					style={{ transitionDelay: '0.1s' }}
				>
					<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
					</svg>
					<span className="hidden sm:inline">Tillbaka till startsidan</span>
				</Link>
				<div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
					<ThemeToggle />
				</div>
			</header>

			<div
				ref={formRef}
				className={`relative z-10 card-glass rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-10 max-w-md w-full transition-all duration-1000 ${
					visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
				}`}
				style={{ transitionDelay: '0.2s' }}
			>
				<h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-6 sm:mb-8 text-center">
					{loginType === 'company' ? 'Företagsinloggning' : 'Logga in'}
				</h1>
				{loginType === 'company' && (
					<p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-4 sm:mb-6 text-center">
						Logga in för att komma åt ditt företags ATS-system
					</p>
				)}
				<div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
					<button
						type="button"
						onClick={() => handleOAuthSignIn('google')}
						disabled={loading || oauthLoading !== null}
						className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
					>
						{oauthLoading === 'google' ? (
							<div className="flex items-center gap-2">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-700 dark:border-slate-200"></div>
								<span>Fortsätter…</span>
							</div>
						) : (
							<span className="flex items-center gap-2">
								<img src="/logos/google.svg" alt="" className="h-5 w-5" />
								<span>Fortsätt med Google</span>
							</span>
						)}
					</button>
					<button
						type="button"
						onClick={() => handleOAuthSignIn('azure')}
						disabled={loading || oauthLoading !== null}
						className="w-full min-h-[44px] inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
					>
						{oauthLoading === 'azure' ? (
							<div className="flex items-center gap-2">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-700 dark:border-slate-200"></div>
								<span>Fortsätter…</span>
							</div>
						) : (
							<span className="flex items-center gap-2">
								<img src="/logos/microsoft.svg" alt="" className="h-5 w-5" />
								<span>Fortsätt med Microsoft</span>
							</span>
						)}
					</button>
					<div className="flex items-center gap-3">
						<div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
						<span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">eller</span>
						<div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
					</div>
				</div>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						onSubmit(new FormData(e.currentTarget));
					}}
					className="space-y-4 sm:space-y-6"
				>
					<div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.4s' }}>
						<label htmlFor="login-email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">E-post</label>
						<input 
							id="login-email"
							name="email" 
							type="email" 
							inputMode="email"
							autoComplete="email"
							placeholder="e‑post" 
							className="input-base w-full" 
							required
							aria-required="true"
						/>
					</div>
					<div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.5s' }}>
						<label htmlFor="login-password" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Lösenord</label>
						<div className="relative">
							<input 
								id="login-password"
								name="password" 
								type={showPassword ? "text" : "password"} 
								autoComplete="current-password"
								placeholder="lösenord" 
								className="input-base w-full pr-10" 
								required
								aria-required="true"
							/>
							<button
								type="button"
								onClick={() => setShowPassword(!showPassword)}
								className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded p-1"
								aria-label={showPassword ? "Dölj lösenord" : "Visa lösenord"}
							>
								{showPassword ? (
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m0 0L12 12m-5.71-5.71L3 3m9.29 9.29L21 21" />
									</svg>
								) : (
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
									</svg>
								)}
							</button>
						</div>
					</div>
					<div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.55s' }}>
						<div className="flex justify-end">
							<Link href="/forgot-password" className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline">
								Glömt lösenord?
							</Link>
						</div>
					</div>
					<div className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.6s' }}>
						<button disabled={loading} className="btn-primary w-full ripple hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-xl relative overflow-hidden group">
							<span className="relative z-10">
								{loading ? (
									<div className="flex items-center justify-center gap-2">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
										<span>Loggar in…</span>
									</div>
								) : 'Logga in'}
							</span>
							<div className="absolute inset-0 bg-gradient-to-r from-primary-700 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
						</button>
					</div>
				</form>
				{passwordResetSuccess && (
					<div className={`mt-4 sm:mt-6 transition-all duration-500 ${passwordResetSuccess ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
						<p className="p-3 sm:p-4 rounded-lg bg-accent-success-50 dark:bg-accent-success-900/20 text-accent-success-700 dark:text-accent-success-400 border border-accent-success-200 dark:border-accent-success-800 text-sm sm:text-base" role="alert">
							Lösenordet har återställts! Du kan nu logga in med ditt nya lösenord.
						</p>
					</div>
				)}
				{error && (
					<div className={`mt-4 sm:mt-6 transition-all duration-500 ${error ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
						<p className="p-3 sm:p-4 rounded-lg bg-accent-error-50 dark:bg-accent-error-900/20 text-accent-error-700 dark:text-accent-error-400 border border-accent-error-200 dark:border-accent-error-800 text-sm sm:text-base" role="alert">
							{error}
						</p>
					</div>
				)}
				<p className={`mt-6 sm:mt-8 text-xs sm:text-sm text-center text-slate-600 dark:text-slate-400 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.7s' }}>
					Har du inget konto?{' '}
					<a className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium underline underline-offset-2 transition-colors" href="/signup">
						Skapa konto
					</a>
				</p>
			</div>
		</main>
	);
}

export default function LoginPage() {
	return <LoginForm />;
}
